import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeaderBar } from '../components/HeaderBar';
import { ProgressStepper } from '../components/ProgressStepper';
import { ActionButton } from '../components/ActionButton';
interface ProposalData {
  id: string;
  item: string;
  unitPrice: string;
  quantity: number;
  totalCost: string;
  deliveryDate: string;
  requirementMatch: {
    status: 'exact' | 'partial' | 'different';
    details?: string;
  };
}
interface ProposalDetails {
  paymentTerms: string;
  deliveryMethod: string;
  warranty: string;
  specialRemarks: string;
}
export const DataVerificationScreen: React.FC = () => {
  const navigate = useNavigate();
  const [editingField, setEditingField] = useState<{
    id: string;
    field: keyof ProposalData;
  } | null>(null);
  const [editingDetail, setEditingDetail] = useState<keyof ProposalDetails | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  // Sample extracted data
  const [proposalData, setProposalData] = useState<ProposalData[]>([{
    id: '1',
    item: 'Dell XPS 14 v2025',
    unitPrice: '915€',
    quantity: 150,
    totalCost: '137.250€',
    deliveryDate: '10/17/2025',
    requirementMatch: {
      status: 'exact',
      details: 'Matches buyer specifications exactly'
    }
  }, {
    id: '2',
    item: 'Extended Warranty Package',
    unitPrice: '0€',
    quantity: 150,
    totalCost: '0€',
    deliveryDate: '10/17/2025',
    requirementMatch: {
      status: 'partial',
      details: 'Offers 2-year warranty instead of requested 3-year warranty'
    }
  }]);
  // Additional proposal details
  const [proposalDetails, setProposalDetails] = useState<ProposalDetails>({
    paymentTerms: 'Net 30 days from invoice date',
    deliveryMethod: 'Direct shipping to corporate headquarters',
    warranty: '2-year on-site service with next business day response',
    specialRemarks: 'Includes pre-installation of company software suite and custom BIOS configuration'
  });
  const handleEdit = (id: string, field: keyof ProposalData, value: string | number) => {
    setEditingField({
      id,
      field
    });
    setEditValue(value.toString());
  };
  const handleEditDetail = (field: keyof ProposalDetails, value: string) => {
    setEditingDetail(field);
    setEditValue(value);
  };
  const handleSave = () => {
    if (editingField) {
      const {
        id,
        field
      } = editingField;
      // Format value based on field type
      let formattedValue: string | number = editValue;
      if (field === 'quantity') {
        formattedValue = parseInt(editValue, 10);
        // Update total cost based on new quantity
        const item = proposalData.find(item => item.id === id);
        if (item) {
          const unitPrice = parseFloat(item.unitPrice.replace(/[^0-9.-]+/g, ''));
          const newTotalCost = unitPrice * formattedValue;
          setProposalData(prevData => prevData.map(item => item.id === id ? {
            ...item,
            [field]: formattedValue,
            totalCost: `${newTotalCost.toLocaleString().replace('.', ',')}€`
          } : item));
        }
      } else {
        setProposalData(prevData => prevData.map(item => item.id === id ? {
          ...item,
          [field]: formattedValue
        } : item));
      }
      setEditingField(null);
    } else if (editingDetail) {
      setProposalDetails(prev => ({
        ...prev,
        [editingDetail]: editValue
      }));
      setEditingDetail(null);
    }
    setHasEdited(true);
  };
  const handleCancel = () => {
    setEditingField(null);
    setEditingDetail(null);
  };
  const calculateTotal = () => {
    return proposalData.reduce((total, item) => {
      const value = parseFloat(item.totalCost.replace(/[^0-9.-]+/g, '').replace(',', '.'));
      return total + value;
    }, 0);
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    // Simulate submission delay
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/negotiation');
    }, 1000);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };
  const getRequirementMatchBadge = (status: string) => {
    switch (status) {
      case 'exact':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#4caf50] bg-opacity-10 text-[#4caf50]" data-id="element-36">
            Exact Match
          </span>;
      case 'partial':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#fbc02d] bg-opacity-10 text-[#fbc02d]" data-id="element-37">
            Partial Match
          </span>;
      case 'different':
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#e53935] bg-opacity-10 text-[#e53935]" data-id="element-38">
            Different
          </span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600" data-id="element-39">
            Unknown
          </span>;
    }
  };
  return <div className="min-h-screen bg-[#f5f6f8]" data-id="element-40">
      <HeaderBar logoSrc="https://uploadthingy.s3.us-west-1.amazonaws.com/tXtrQ38XxwUBTwmzoNN8ZL/Prospeum_Logo_White_SVG.svg" logoAlt="Prospeum Logo" data-id="element-41" />
      <main className="container mx-auto px-4 py-8" data-id="element-42">
        <div className="mb-8" data-id="element-43">
          <h1 className="text-2xl font-bold text-[#333333] mb-6" data-id="element-44">
            Q4 Marketing Laptop Refresh - Verify Details
          </h1>
          <ProgressStepper steps={['Submit Proposal', 'Verify Details', 'Submission Complete']} currentStep={1} data-id="element-45" />
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-46">
          <div className="mb-6 text-center" data-id="element-47">
            <h2 className="text-xl font-bold text-[#333333] mb-2" data-id="element-48">
              Please Verify Our Understanding
            </h2>
            <p className="text-[#333333]" data-id="element-49">
              Our AI has extracted the following key information from your
              proposal. Please review it for accuracy and make any necessary
              edits before submitting.
            </p>
          </div>
          {hasEdited && <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded mb-6" data-id="element-50">
              <div className="flex" data-id="element-51">
                <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-52">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-53" />
                </svg>
                <span data-id="element-54">
                  Changes detected. Please review the updated totals before
                  submitting.
                </span>
              </div>
            </div>}
          <div className="overflow-x-auto mb-8" data-id="element-55">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-56">
              <thead className="bg-[#f5f6f8]" data-id="element-57">
                <tr data-id="element-58">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-59">
                    Item
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-60">
                    Unit Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-61">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-62">
                    Total Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-63">
                    Delivery Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-64">
                    Requirements Match
                  </th>
                  <th scope="col" className="relative px-6 py-3" data-id="element-65">
                    <span className="sr-only" data-id="element-66">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-67">
                {proposalData.map(item => <tr key={item.id} className={editingField?.id === item.id ? 'bg-blue-50' : ''} data-id="element-68">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#333333]" data-id="element-69">
                      {editingField?.id === item.id && editingField.field === 'item' ? <input type="text" className="border border-gray-300 rounded-md px-2 py-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-70" /> : <span className="cursor-pointer group relative" onClick={() => handleEdit(item.id, 'item', item.item)} data-id="element-71">
                          {item.item}
                          <svg className="w-4 h-4 ml-1 inline-block text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-72">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" data-id="element-73" />
                          </svg>
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#333333]" data-id="element-74">
                      {editingField?.id === item.id && editingField.field === 'unitPrice' ? <input type="text" className="border border-gray-300 rounded-md px-2 py-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-75" /> : <span className="cursor-pointer group relative" onClick={() => handleEdit(item.id, 'unitPrice', item.unitPrice)} data-id="element-76">
                          {item.unitPrice}
                          <svg className="w-4 h-4 ml-1 inline-block text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-77">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" data-id="element-78" />
                          </svg>
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#333333]" data-id="element-79">
                      {editingField?.id === item.id && editingField.field === 'quantity' ? <input type="number" className="border border-gray-300 rounded-md px-2 py-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-80" /> : <span className="cursor-pointer group relative" onClick={() => handleEdit(item.id, 'quantity', item.quantity)} data-id="element-81">
                          {item.quantity}
                          <svg className="w-4 h-4 ml-1 inline-block text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-82">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" data-id="element-83" />
                          </svg>
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#333333]" data-id="element-84">
                      {item.totalCost}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#333333]" data-id="element-85">
                      {editingField?.id === item.id && editingField.field === 'deliveryDate' ? <input type="text" className="border border-gray-300 rounded-md px-2 py-1 w-full" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus data-id="element-86" /> : <span className="cursor-pointer group relative" onClick={() => handleEdit(item.id, 'deliveryDate', item.deliveryDate)} data-id="element-87">
                          {item.deliveryDate}
                          <svg className="w-4 h-4 ml-1 inline-block text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-88">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" data-id="element-89" />
                          </svg>
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-90">
                      <div className="flex flex-col" data-id="element-91">
                        {getRequirementMatchBadge(item.requirementMatch.status)}
                        {item.requirementMatch.details && <span className="text-xs text-gray-500 mt-1" data-id="element-92">
                            {item.requirementMatch.details}
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-93">
                      {editingField?.id === item.id ? <div className="flex space-x-2" data-id="element-94">
                          <button onClick={handleSave} className="text-[#4caf50] hover:text-[#3d8b40]" title="Save" data-id="element-95">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-96">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-97" />
                            </svg>
                          </button>
                          <button onClick={handleCancel} className="text-red-500 hover:text-red-700" title="Cancel" data-id="element-98">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-99">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-100" />
                            </svg>
                          </button>
                        </div> : <button onClick={() => handleEdit(item.id, 'item', item.item)} className="text-[#005a9e] hover:text-[#004a85]" title="Edit row" data-id="element-101">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-102">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" data-id="element-103" />
                          </svg>
                        </button>}
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-[#f5f6f8]" data-id="element-104">
                <tr data-id="element-105">
                  <td colSpan={3} className="px-6 py-4 text-right font-medium text-[#333333]" data-id="element-106">
                    Total:
                  </td>
                  <td className="px-6 py-4 font-bold text-[#333333]" data-id="element-107">
                    137.250€
                  </td>
                  <td colSpan={3} data-id="element-108"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Additional Proposal Details Section */}
          <div className="bg-[#f5f6f8] rounded-lg p-6 mb-6 border border-gray-200" data-id="element-109">
            <h3 className="text-lg font-medium text-[#333333] mb-4 flex items-center" data-id="element-110">
              <svg className="w-5 h-5 mr-2 text-[#005a9e]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-111">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" data-id="element-112" />
              </svg>
              Additional Proposal Details
            </h3>
            <div className="space-y-4" data-id="element-113">
              <div className={`p-4 rounded-md ${editingDetail === 'paymentTerms' ? 'bg-blue-50' : 'bg-white'}`} data-id="element-114">
                <div className="flex justify-between items-center mb-1" data-id="element-115">
                  <span className="text-sm font-medium text-gray-500" data-id="element-116">
                    Payment Terms
                  </span>
                  {editingDetail === 'paymentTerms' ? <div className="flex space-x-2" data-id="element-117">
                      <button onClick={handleSave} className="text-[#4caf50] hover:text-[#3d8b40]" title="Save" data-id="element-118">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-119">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-120" />
                        </svg>
                      </button>
                      <button onClick={handleCancel} className="text-red-500 hover:text-red-700" title="Cancel" data-id="element-121">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-122">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-123" />
                        </svg>
                      </button>
                    </div> : <button onClick={() => handleEditDetail('paymentTerms', proposalDetails.paymentTerms)} className="text-[#005a9e] hover:text-[#004a85]" title="Edit payment terms" data-id="element-124">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-125">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" data-id="element-126" />
                      </svg>
                    </button>}
                </div>
                {editingDetail === 'paymentTerms' ? <textarea className="w-full border border-gray-300 rounded-md px-3 py-2" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} rows={2} autoFocus data-id="element-127" /> : <p className="text-[#333333]" data-id="element-128">
                    {proposalDetails.paymentTerms}
                  </p>}
              </div>
              <div className={`p-4 rounded-md ${editingDetail === 'deliveryMethod' ? 'bg-blue-50' : 'bg-white'}`} data-id="element-129">
                <div className="flex justify-between items-center mb-1" data-id="element-130">
                  <span className="text-sm font-medium text-gray-500" data-id="element-131">
                    Delivery Method
                  </span>
                  {editingDetail === 'deliveryMethod' ? <div className="flex space-x-2" data-id="element-132">
                      <button onClick={handleSave} className="text-[#4caf50] hover:text-[#3d8b40]" title="Save" data-id="element-133">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-134">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-135" />
                        </svg>
                      </button>
                      <button onClick={handleCancel} className="text-red-500 hover:text-red-700" title="Cancel" data-id="element-136">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-137">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-138" />
                        </svg>
                      </button>
                    </div> : <button onClick={() => handleEditDetail('deliveryMethod', proposalDetails.deliveryMethod)} className="text-[#005a9e] hover:text-[#004a85]" title="Edit delivery method" data-id="element-139">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-140">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" data-id="element-141" />
                      </svg>
                    </button>}
                </div>
                {editingDetail === 'deliveryMethod' ? <textarea className="w-full border border-gray-300 rounded-md px-3 py-2" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} rows={2} autoFocus data-id="element-142" /> : <p className="text-[#333333]" data-id="element-143">
                    {proposalDetails.deliveryMethod}
                  </p>}
              </div>
              <div className={`p-4 rounded-md ${editingDetail === 'warranty' ? 'bg-blue-50' : 'bg-white'}`} data-id="element-144">
                <div className="flex justify-between items-center mb-1" data-id="element-145">
                  <span className="text-sm font-medium text-gray-500" data-id="element-146">
                    Warranty Details
                  </span>
                  {editingDetail === 'warranty' ? <div className="flex space-x-2" data-id="element-147">
                      <button onClick={handleSave} className="text-[#4caf50] hover:text-[#3d8b40]" title="Save" data-id="element-148">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-149">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-150" />
                        </svg>
                      </button>
                      <button onClick={handleCancel} className="text-red-500 hover:text-red-700" title="Cancel" data-id="element-151">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-152">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-153" />
                        </svg>
                      </button>
                    </div> : <button onClick={() => handleEditDetail('warranty', proposalDetails.warranty)} className="text-[#005a9e] hover:text-[#004a85]" title="Edit warranty details" data-id="element-154">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-155">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" data-id="element-156" />
                      </svg>
                    </button>}
                </div>
                {editingDetail === 'warranty' ? <textarea className="w-full border border-gray-300 rounded-md px-3 py-2" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} rows={2} autoFocus data-id="element-157" /> : <p className="text-[#333333]" data-id="element-158">{proposalDetails.warranty}</p>}
              </div>
              <div className={`p-4 rounded-md ${editingDetail === 'specialRemarks' ? 'bg-blue-50' : 'bg-white'}`} data-id="element-159">
                <div className="flex justify-between items-center mb-1" data-id="element-160">
                  <span className="text-sm font-medium text-gray-500" data-id="element-161">
                    Special Remarks
                  </span>
                  {editingDetail === 'specialRemarks' ? <div className="flex space-x-2" data-id="element-162">
                      <button onClick={handleSave} className="text-[#4caf50] hover:text-[#3d8b40]" title="Save" data-id="element-163">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-164">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-165" />
                        </svg>
                      </button>
                      <button onClick={handleCancel} className="text-red-500 hover:text-red-700" title="Cancel" data-id="element-166">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-167">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-168" />
                        </svg>
                      </button>
                    </div> : <button onClick={() => handleEditDetail('specialRemarks', proposalDetails.specialRemarks)} className="text-[#005a9e] hover:text-[#004a85]" title="Edit special remarks" data-id="element-169">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-170">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" data-id="element-171" />
                      </svg>
                    </button>}
                </div>
                {editingDetail === 'specialRemarks' ? <textarea className="w-full border border-gray-300 rounded-md px-3 py-2" value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleKeyDown} rows={3} autoFocus data-id="element-172" /> : <p className="text-[#333333]" data-id="element-173">
                    {proposalDetails.specialRemarks}
                  </p>}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 space-y-4 sm:space-y-0" data-id="element-174">
            <ActionButton label="Back to Upload" onClick={() => navigate('/submit-proposal')} variant="secondary" data-id="element-175" />
            <ActionButton label={isSubmitting ? 'Processing...' : 'Confirm & Submit Proposal'} onClick={handleSubmit} disabled={isSubmitting} icon={isSubmitting ? <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-177">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-178"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-179"></path>
                  </svg> : undefined} data-id="element-176" />
          </div>
        </div>
      </main>
    </div>;
};